import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ClockIcon,
  EyeIcon,
  XIcon,
} from "@heroicons/react/outline";
import QuickOnlineAccountServices from "../services/QuickOnlineAccountServices";
import Paginate from "../utils/Paginate";
import { formatDate, DisplayTime, validateDate } from "../utils/convertDate";

const durationStyles = {
  fast: "bg-green-100 text-green-800",
  medium: "bg-yellow-100 text-yellow-800",
  slow: "bg-red-100 text-red-800",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getDurationStyle(ms) {
  if (!ms) return "bg-gray-100 text-gray-800";
  if (ms < 5000) return durationStyles.fast;
  if (ms < 15000) return durationStyles.medium;
  return durationStyles.slow;
}

function normalizeSelfieUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const withoutQuery = raw.split("?")[0];
  try {
    return decodeURIComponent(withoutQuery).toLowerCase();
  } catch (error) {
    return withoutQuery.toLowerCase();
  }
}

function getSelfieFileName(value) {
  const normalized = normalizeSelfieUrl(value);
  return normalized.split("/").pop() || "";
}

function isSelfieMatch(row, targetUrl) {
  const rowUrl = normalizeSelfieUrl(row?.selfImageUrl);
  const target = normalizeSelfieUrl(targetUrl);
  if (!rowUrl || !target) return false;
  if (rowUrl === target) return true;
  const rowFile = getSelfieFileName(rowUrl);
  const targetFile = getSelfieFileName(target);
  return !!rowFile && rowFile === targetFile;
}

export default function LivenessImagesPanel({
  searchTerm,
  matchSelfieUrl,
  appName,
  customerId,
  title = "Liveness Images",
}) {
  const search = String(searchTerm || "").trim();
  const matchUrl = String(matchSelfieUrl || "").trim();
  const matchMode = !!matchUrl;
  const hasQuery = matchMode || !!search;

  const [images, setImages] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const requestRef = useRef(0);

  const clearResults = useCallback(() => {
    setImages([]);
    setTotalCount(0);
    setTotalPages(0);
    setHasNextPage(false);
    setHasPreviousPage(false);
  }, []);

  const fetchSearchPage = useCallback(
    async (term, page) => {
      if (!term || !appName) {
        clearResults();
        setLoading(false);
        return;
      }

      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const tenantId = sessionStorage.getItem("tenantId");
        const resp = await QuickOnlineAccountServices.listLivenessImages(
          appName,
          tenantId,
          "",
          "",
          page,
          10,
          term,
        );

        if (requestId !== requestRef.current) return;

        if (resp?.data) {
          const result = resp.data.result || [];
          setImages(result);
          setTotalCount(resp.data.totalCount ?? result.length);
          setTotalPages(resp.data.totalPages ?? 0);
          setHasNextPage(!!resp.data.hasNextPage);
          setHasPreviousPage(!!resp.data.hasPreviousPage);
        } else {
          clearResults();
        }
      } catch (error) {
        if (requestId !== requestRef.current) return;
        console.error("Error loading liveness images:", error);
        clearResults();
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
        }
      }
    },
    [appName, clearResults],
  );

  const fetchAndMatchSelfie = useCallback(
    async (targetUrl) => {
      if (!targetUrl || !appName) {
        clearResults();
        setLoading(false);
        return;
      }

      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const tenantId = sessionStorage.getItem("tenantId");
        const resp = await QuickOnlineAccountServices.listLivenessImages(
          appName,
          tenantId,
          "",
          "",
          1,
          10,
          targetUrl,
        );

        if (requestId !== requestRef.current) return;

        const result = resp?.data?.result || [];
        const matches = result
          .filter((row) => isSelfieMatch(row, targetUrl))
          .map((row) => ({
            ...row,
            sourceAppName: row.appName || appName,
          }));

        const seen = new Set();
        const deduped = matches.filter((row) => {
          const key =
            row.sessionId || row.id || row.selfImageUrl || `${row.createdOn}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        deduped.sort(
          (a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0),
        );

        setImages(deduped);
        setTotalCount(deduped.length);
        setTotalPages(Math.ceil(deduped.length / 10));
        setHasNextPage(false);
        setHasPreviousPage(false);
      } catch (error) {
        if (requestId !== requestRef.current) return;
        console.error("Error loading liveness images:", error);
        clearResults();
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
        }
      }
    },
    [appName, customerId, clearResults],
  );

  useEffect(() => {
    setPageIndex(1);
    clearResults();
  }, [search, matchUrl, appName, customerId, clearResults]);

  useEffect(() => {
    if (!matchMode) return;
    fetchAndMatchSelfie(matchUrl);
  }, [matchMode, matchUrl, fetchAndMatchSelfie]);

  useEffect(() => {
    if (matchMode) return;
    fetchSearchPage(search, pageIndex);
  }, [matchMode, search, pageIndex, fetchSearchPage]);

  const localTotalPages = matchMode ? Math.ceil(images.length / 10) : 0;
  const safePageIndex = matchMode
    ? Math.min(pageIndex, Math.max(localTotalPages, 1))
    : pageIndex;
  const displayImages = matchMode
    ? images.slice((safePageIndex - 1) * 10, safePageIndex * 10)
    : images;
  const displayTotalCount = matchMode ? images.length : totalCount;
  const displayTotalPages = matchMode ? localTotalPages : totalPages;
  const displayHasNextPage = matchMode
    ? safePageIndex < localTotalPages
    : hasNextPage;
  const displayHasPreviousPage = matchMode
    ? safePageIndex > 1
    : hasPreviousPage;

  useEffect(() => {
    if (!matchMode) return;
    if (pageIndex > Math.max(localTotalPages, 1)) {
      setPageIndex(Math.max(localTotalPages, 1));
    }
  }, [matchMode, localTotalPages, pageIndex]);

  const viewImage = (image) => {
    setSelectedImage(image);
    setShowModal(true);
  };

  return (
    <div className="pt-4 border-t border-gray-200 mt-4">
      <h5 className="text-sm font-semibold text-gray-700 mb-3">{title}</h5>

      {!hasQuery ? (
        <div className="text-center py-4 text-sm text-gray-500">
          No search value available to filter liveness images.
        </div>
      ) : loading ? (
        <div className="text-center py-4 text-sm text-gray-500">
          Loading liveness images...
        </div>
      ) : displayImages.length === 0 ? (
        <div className="text-center py-4 text-sm text-gray-500">
          {matchMode
            ? "No liveness images found matching this customer's selfie."
            : "No liveness images found for this customer."}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th className="py-3.5 pr-3 text-left text-sm italic font-normal text-gray-500">
                    Self Image
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                    Session ID
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                    Liveness Duration
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                    Challenges
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-500">
                    Date Created
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm italic font-normal text-gray-900">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {displayImages.map((item, index) => (
                  <tr
                    key={
                      item.sessionId ||
                      item.id ||
                      item.selfImageUrl ||
                      `${item.sourceAppName}-${index}`
                    }
                  >
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                      {item.base64Image || item.selfImageUrl ? (
                        <div
                          className="relative h-12 w-12 rounded-full overflow-hidden border border-rose-600 cursor-pointer"
                          onClick={() => viewImage(item)}
                        >
                          <img
                            src={item.base64Image || item.selfImageUrl}
                            alt="Liveness"
                            className="h-12 w-12 rounded-full object-cover"
                          />
                        </div>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                      {item.sessionId || "N/A"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <span
                        className={classNames(
                          getDurationStyle(item.livenessDurationMs),
                          "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium",
                        )}
                      >
                        {item.livenessDurationMs != null
                          ? `${(item.livenessDurationMs / 1000).toFixed(2)}s`
                          : "N/A"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                      <span className="inline-flex items-center text-gray-500">
                        <CheckCircleIcon className="h-4 w-4 mr-1.5 text-green-500" />
                        {item.listOfCompletedChallenges
                          ? item.listOfCompletedChallenges.length
                          : 0}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                      {formatDate(item.createdOn)}
                    </td>
                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <button
                        type="button"
                        onClick={() => viewImage(item)}
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Paginate
            nextPage={displayHasNextPage}
            pageIndex={safePageIndex}
            totalCount={displayTotalCount}
            totalPages={displayTotalPages}
            prevPage={displayHasPreviousPage}
            setPageIndex={setPageIndex}
          />
        </>
      )}

      <Transition.Root show={showModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowModal(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-w-lg bg-white rounded-lg px-6 py-6 text-left shadow-xl overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium text-gray-900"
                    >
                      Liveness Image Detail
                    </Dialog.Title>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {selectedImage && (
                    <div className="space-y-4">
                      {(selectedImage.selfImageUrl ||
                        selectedImage.base64Image) && (
                        <div className="flex justify-center">
                          <img
                            src={
                              selectedImage.selfImageUrl ||
                              selectedImage.base64Image
                            }
                            alt="Selfie"
                            className="h-64 w-64 object-cover rounded-lg border border-gray-200"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Session ID</p>
                          <p className="text-sm font-medium text-gray-900 break-all">
                            {selectedImage.sessionId || "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">App Name</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedImage.appName ||
                              selectedImage.sourceAppName ||
                              "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Liveness Duration
                          </p>
                          <p className="text-sm font-medium text-gray-900 flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                            {selectedImage.livenessDurationMs != null
                              ? `${(
                                  selectedImage.livenessDurationMs / 1000
                                ).toFixed(2)}s`
                              : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date Created</p>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDate(selectedImage.createdOn)}
                          </p>
                        </div>
                      </div>

                      {selectedImage.completedChallengesImages &&
                        selectedImage.completedChallengesImages.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Liveness Challenge Images
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                              {selectedImage.completedChallengesImages.map(
                                (img, index) => (
                                  <div key={index} className="text-center">
                                    <img
                                      src={img.imageUrl}
                                      alt={img.challenge}
                                      className="h-20 w-20 object-cover rounded-lg border border-gray-200 mx-auto"
                                    />
                                    <p className="mt-1 text-[10px] font-medium text-gray-700 capitalize truncate">
                                      {String(img.challenge || "").replace(
                                        /_/g,
                                        " ",
                                      )}
                                    </p>
                                    <p className="text-[10px] text-gray-500">
                                      {validateDate(img.capturedAt)} ·{" "}
                                      {DisplayTime(img.capturedAt)}
                                    </p>
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {selectedImage.listOfCompletedChallenges &&
                        selectedImage.listOfCompletedChallenges.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-gray-900 mb-2">
                              Completed Challenges
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {selectedImage.listOfCompletedChallenges.map(
                                (challenge, index) => (
                                  <span
                                    key={index}
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                  >
                                    <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                                    {challenge}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}
